import { z } from 'zod';
import { ID, TextStyle } from './timeline.js';
/**
 * A brand kit is the reusable half of "branded content": colours, fonts, logo
 * and a set of templates (lower third, title card, outro) that a clip can
 * instantiate by id. Templates are declarative so that both the browser preview
 * and the ffmpeg renderer can draw them from the same description.
 */
/**
 * A colour in a template: either a literal, or a reference to one of the kit's
 * own colours. The reference is the point of a brand kit -- change the primary
 * colour once and every lower third follows. A literal hex stays allowed for
 * the odd accent that is not the brand colour.
 */
export const BrandColor = z.union([
    z.string().regex(/^#[0-9a-fA-F]{6}$/),
    z.literal('primary'),
    z.literal('secondary'),
]);
export function resolveBrandColor(kit, value) {
    if (value === 'primary')
        return kit.primary;
    if (value === 'secondary')
        return kit.secondary;
    return value;
}
export const BrandLayer = z.discriminatedUnion('type', [
    z.object({
        type: z.literal('text'),
        /** Name of the slot a user fills in, e.g. "name". */
        slot: z.string().max(40),
        /** Shown when the slot is empty. */
        placeholder: z.string().max(200).default(''),
        style: TextStyle,
        /** Overrides `style.color` when set; this is what lets text follow the kit. */
        color: BrandColor.nullable().default(null),
        x: z.number().min(-1).max(2),
        y: z.number().min(-1).max(2),
        /**
         * How wide the text is meant to be, as a fraction of the frame. The
         * renderer does not wrap -- drawtext draws one line and lets it run -- so
         * this is what the editor measures against to warn that a value is too
         * long, not a constraint anything enforces.
         */
        maxWidth: z.number().min(0.05).max(1).default(0.8),
    }),
    z.object({
        type: z.literal('rect'),
        color: BrandColor,
        opacity: z.number().min(0).max(1).default(1),
        x: z.number(),
        y: z.number(),
        w: z.number().min(0).max(2),
        h: z.number().min(0).max(2),
    }),
    z.object({
        type: z.literal('logo'),
        x: z.number(),
        y: z.number(),
        /** Fraction of canvas width. */
        w: z.number().min(0.01).max(1).default(0.18),
        opacity: z.number().min(0).max(1).default(1),
    }),
]);
export const BrandTemplate = z.object({
    id: ID,
    name: z.string().max(80),
    kind: z.enum(['lowerThird', 'title', 'outro', 'bug']),
    /** Suggested on-screen duration in seconds. */
    defaultDuration: z.number().min(0.5).max(30).default(4),
    animation: z.enum(['none', 'fade', 'slideLeft', 'slideUp']).default('fade'),
    layers: z.array(BrandLayer).max(12),
});
export const BrandKit = z.object({
    id: ID,
    name: z.string().max(80),
    primary: z.string().regex(/^#[0-9a-fA-F]{6}$/).default('#e11d48'),
    secondary: z.string().regex(/^#[0-9a-fA-F]{6}$/).default('#0f172a'),
    fontFamily: z.string().max(80).default('Inter'),
    /** Path inside the media store, filled by the API when a logo is uploaded. */
    logoPath: z.string().nullable().default(null),
    /** Path to a .ttf; null falls back to the container's default font. */
    fontPath: z.string().nullable().default(null),
    templates: z.array(BrandTemplate).max(50).default([]),
});
export function defaultBrandKit(id = 'default') {
    return BrandKit.parse({
        id,
        name: 'Standard',
        templates: [
            {
                id: 'lower-third',
                name: 'Bauchbinde',
                kind: 'lowerThird',
                defaultDuration: 4,
                animation: 'slideLeft',
                layers: [
                    // Sits above the default subtitle band (y 0.82) so the two never collide.
                    { type: 'rect', color: 'primary', opacity: 0.95, x: 0.06, y: 0.655, w: 0.012, h: 0.115 },
                    { type: 'rect', color: 'secondary', opacity: 0.75, x: 0.072, y: 0.655, w: 0.62, h: 0.115 },
                    {
                        type: 'text',
                        slot: 'name',
                        placeholder: 'Vorname Nachname',
                        x: 0.095,
                        y: 0.672,
                        maxWidth: 0.55,
                        style: { size: 0.032, color: '#ffffff', align: 'left', bold: true, boxColor: null },
                    },
                    {
                        type: 'text',
                        slot: 'role',
                        placeholder: 'Funktion',
                        x: 0.095,
                        y: 0.716,
                        maxWidth: 0.55,
                        style: { size: 0.024, color: '#e2e8f0', align: 'left', boxColor: null },
                    },
                ],
            },
            {
                id: 'title-card',
                name: 'Titel',
                kind: 'title',
                defaultDuration: 3,
                animation: 'fade',
                layers: [
                    { type: 'rect', color: 'secondary', opacity: 1, x: 0, y: 0, w: 1, h: 1 },
                    {
                        type: 'text',
                        slot: 'title',
                        placeholder: 'Titel',
                        x: 0.5,
                        y: 0.44,
                        style: { size: 0.085, color: '#ffffff', align: 'center', bold: true, boxColor: null },
                    },
                    {
                        type: 'text',
                        slot: 'subtitle',
                        placeholder: 'Untertitel',
                        x: 0.5,
                        y: 0.56,
                        color: 'primary',
                        style: { size: 0.035, align: 'center', boxColor: null },
                    },
                ],
            },
            {
                id: 'bug',
                name: 'Sender-Logo',
                kind: 'bug',
                defaultDuration: 10,
                animation: 'fade',
                layers: [{ type: 'logo', x: 0.86, y: 0.06, w: 0.1, opacity: 0.9 }],
            },
        ],
    });
}
export function markenKasten(template, fields, seiten, mitLogo) {
    let x0 = Infinity;
    let y0 = Infinity;
    let x1 = -Infinity;
    let y1 = -Infinity;
    const nimm = (x, y, w, h) => {
        x0 = Math.min(x0, x);
        y0 = Math.min(y0, y);
        x1 = Math.max(x1, x + w);
        y1 = Math.max(y1, y + h);
    };
    for (const layer of template.layers) {
        if (layer.type === 'rect') {
            nimm(layer.x, layer.y, layer.w, layer.h);
        }
        else if (layer.type === 'logo') {
            if (mitLogo)
                nimm(layer.x, layer.y, layer.w, layer.w * seiten);
        }
        else {
            const value = fields[layer.slot] ?? layer.placeholder;
            if (!value.trim())
                continue;
            const zeilen = value.split('\n');
            const laengste = Math.max(...zeilen.map((z) => z.length));
            const w = (laengste * 0.56 * layer.style.size) / seiten;
            const h = zeilen.length * 1.2 * layer.style.size;
            const rand = layer.style.boxColor ? 0.35 * layer.style.size : 0;
            const links = layer.style.align === 'center' ? layer.x - w / 2 : layer.style.align === 'right' ? layer.x - w : layer.x;
            nimm(links - rand / seiten, layer.y - rand, w + (2 * rand) / seiten, h + 2 * rand);
        }
    }
    if (!Number.isFinite(x0))
        return { x: 0, y: 0, w: 1, h: 1 };
    return { x: x0, y: y0, w: x1 - x0, h: y1 - y0 };
}
/** Wie weit die Mitte des Kastens neben der Bildmitte liegt, in Anteilen. */
export function markenVersatz(k) {
    return { dx: k.x + k.w / 2 - 0.5, dy: k.y + k.h / 2 - 0.5 };
}
/**
 * Wohin die **Mitte des ganzen Bildes** einer Markenebene kommt, wenn sie um
 * die Mitte ihres Inhalts gedreht und skaliert wird.
 *
 * Um einen Punkt `c` drehen heisst: um die Bildmitte drehen und danach um
 * `v - s·R·v` schieben, mit `v` = `c` minus Bildmitte. Gerechnet in Pixeln
 * (sonst verzöge die Drehung im Hochformat), zurück in Anteilen. Ohne Grösse
 * und Drehung ist das genau `x + dx`, `y + dy` -- und bei `x = y = 0.5` die
 * Stelle, an der die Vorlage ihre Teile aufgestellt hat.
 */
export function markenLage(x, y, scale, rotateGrad, versatz, seiten) {
    const a = (rotateGrad * Math.PI) / 180;
    const vx = versatz.dx * seiten;
    const vy = versatz.dy;
    const rx = Math.cos(a) * vx - Math.sin(a) * vy;
    const ry = Math.sin(a) * vx + Math.cos(a) * vy;
    return { x: x + (vx - scale * rx) / seiten, y: y + (vy - scale * ry) };
}
//# sourceMappingURL=brand.js.map